/*
 * Lays its children out at a fixed design width (the page as tuned to its
 * mockup photo, without the product sidebar) and scales them as one picture to
 * the width available, so a composition keeps the photo's proportions beside
 * the sidebar instead of reflowing. The box takes the scaled height, so nothing
 * below overlaps. A transform keeps hit-testing, focus and text intact.
 *
 * Below `minWidth` (or without ResizeObserver, e.g. in tests) the children lay
 * out at the real width and their own responsive rules apply. The inner box is
 * a size container named "fit", so those rules are container queries on it and
 * see the design width while scaled.
 */
import { useLayoutEffect, useRef, useState } from 'react';

export default function FitToWidth({ designWidth, minWidth, maxScale = 1.2, className, children }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  // { scale, height } while scaled; null at natural size.
  const [fit, setFit] = useState(null);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner || typeof ResizeObserver === 'undefined') return undefined;

    const update = () => {
      const width = outer.clientWidth;
      if (width < minWidth) {
        setFit(null);
        return;
      }
      const scale = Math.min(maxScale, width / designWidth);
      const height = inner.offsetHeight; // layout height, before the transform
      setFit((prev) =>
        prev && Math.abs(prev.scale - scale) < 0.0005 && prev.height === height ? prev : { scale, height },
      );
    };

    const observer = new ResizeObserver(update);
    observer.observe(outer);
    observer.observe(inner);
    update();
    return () => observer.disconnect();
  }, [designWidth, minWidth, maxScale]);

  const classes = ['fit'];
  if (fit) classes.push('is-scaled');
  if (className) classes.push(className);

  return (
    <div
      ref={outerRef}
      className={classes.join(' ')}
      style={fit ? { height: Math.ceil(fit.height * fit.scale) } : undefined}
    >
      <div
        ref={innerRef}
        className="fit__in"
        style={fit ? { width: designWidth, transform: `scale(${fit.scale})` } : undefined}
      >
        {children}
      </div>
    </div>
  );
}
